import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Gear, ArrowsClockwise, CheckCircle, Clock, Database, ClockCountdown,
} from "@phosphor-icons/react";

const PROPERTY_TYPES = [
    { key: "appartamento", label: "Appartamento" },
    { key: "casa_indipendente", label: "Casa indipendente" },
    { key: "villetta", label: "Villetta" },
    { key: "casa_schiera", label: "Casa a schiera" },
    { key: "monolocale", label: "Monolocale" },
    { key: "bilocale", label: "Bilocale" },
    { key: "trilocale", label: "Trilocale" },
    { key: "quadrilocale", label: "Quadrilocale" },
];

const overline = "text-[11px] uppercase tracking-[0.15em] font-bold text-gray-500";

export const RadarSettingsPanel = ({ onScan, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState(null);
    const [available, setAvailable] = useState([]);
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [sRes, stRes, lRes] = await Promise.all([
                api.get("/radar/settings"),
                api.get("/radar/scan-status"),
                api.get("/radar/request-log", { params: { limit: 12 } }),
            ]);
            const { available_sources, ...rest } = sRes.data;
            setSettings(rest);
            setAvailable(available_sources || []);
            setStatus(stRes.data);
            setLogs(lRes.data || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    }, []);

    useEffect(() => {
        if (open) loadAll();
    }, [open, loadAll]);

    // Poll status while a scan is running
    useEffect(() => {
        if (!open || !status?.is_running) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/radar/scan-status");
                setStatus(data);
            } catch { /* ignore */ }
        }, 5000);
        return () => clearInterval(t);
    }, [open, status?.is_running]);

    const toggleArr = (key, value) => {
        setSettings((s) => {
            const arr = new Set(s[key] || []);
            if (arr.has(value)) arr.delete(value); else arr.add(value);
            return { ...s, [key]: Array.from(arr) };
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/radar/settings", settings);
            toast.success("Impostazioni Radar salvate");
            onSaved?.();
            await loadAll();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const triggerScan = async () => {
        await onScan?.();
        setTimeout(loadAll, 1500);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="radar-settings-open-btn"
                    className="h-11 px-5 flex items-center gap-2 border border-gray-300 bg-white text-sm font-semibold hover:border-black transition-colors"
                >
                    <Gear size={16} weight="bold" />
                    Impostazioni
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="radar-settings-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display-black text-2xl flex items-center gap-2">
                        <Gear size={22} weight="fill" /> Impostazioni Radar
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                        Controlla fonti, aree, filtri e frequenza delle scansioni automatiche.
                    </DialogDescription>
                </DialogHeader>

                {!settings ? (
                    <div className="py-12 text-center text-sm text-gray-500">Caricamento…</div>
                ) : (
                    <div className="space-y-8 pt-2">
                        {/* Scheduler status */}
                        {status && (
                            <section data-testid="radar-scheduler-status">
                                <div className={overline + " mb-3"}>Stato scheduler</div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <StatusTile
                                        icon={status.is_running ? ArrowsClockwise : CheckCircle}
                                        spin={status.is_running}
                                        label="Scansione"
                                        value={status.is_running ? "In corso…" : "Inattiva"}
                                    />
                                    <StatusTile icon={Database} label="Trovate oggi" value={status.found_today ?? 0} />
                                    <StatusTile
                                        icon={Clock}
                                        label="Ultimo aggiornamento"
                                        value={status.finished_at ? new Date(status.finished_at).toLocaleString("it-IT") : "—"}
                                    />
                                    <StatusTile
                                        icon={ClockCountdown}
                                        label="Auto-scan giornaliero"
                                        value={status.daily_auto_scan ? `ON · h${status.daily_scan_hour_utc} UTC` : "OFF"}
                                    />
                                </div>
                                {status.last_result && (
                                    <p className="text-xs text-gray-500 mt-2" data-testid="radar-last-result">
                                        Ultima scansione: {status.last_result.new} nuove ·{" "}
                                        {status.last_result.updated} aggiornate ·{" "}
                                        {status.last_result.auto_dismissed} sotto soglia · fonti{" "}
                                        {(status.last_result.sources_used || []).join(", ")}
                                    </p>
                                )}
                                <button
                                    onClick={triggerScan}
                                    disabled={status.is_running}
                                    data-testid="radar-settings-scan-btn"
                                    className="mt-3 h-10 px-4 flex items-center gap-2 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                                >
                                    <ArrowsClockwise size={15} weight="bold" className={status.is_running ? "animate-spin" : ""} />
                                    {status.is_running ? "Scansione in corso…" : "Esegui scansione ora"}
                                </button>
                            </section>
                        )}

                        {/* Sources */}
                        <section data-testid="radar-sources-section">
                            <div className={overline + " mb-3"}>Fonti attive</div>
                            <div className="space-y-2">
                                {available.map((src) => {
                                    const active = (settings.active_sources || []).includes(src.source_id);
                                    return (
                                        <div key={src.source_id} className="flex items-center justify-between border border-gray-200 px-4 py-2.5">
                                            <div>
                                                <div className="text-sm font-semibold flex items-center gap-2">
                                                    {src.display_name}
                                                    {src.is_demo && <span className="bg-gray-100 text-gray-500 text-[9px] font-bold uppercase px-1.5 py-0.5">demo</span>}
                                                </div>
                                                <div className="text-[11px] text-gray-400 font-mono-tight">{src.source_id}</div>
                                            </div>
                                            <Switch
                                                checked={active}
                                                onCheckedChange={() => toggleArr("active_sources", src.source_id)}
                                                data-testid={`radar-source-toggle-${src.source_id}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Property types */}
                        <section data-testid="radar-types-section">
                            <div className={overline + " mb-3"}>Categorie immobili</div>
                            <div className="flex flex-wrap gap-2">
                                {PROPERTY_TYPES.map((t) => {
                                    const active = (settings.allowed_property_types || []).includes(t.key);
                                    return (
                                        <button
                                            key={t.key}
                                            onClick={() => toggleArr("allowed_property_types", t.key)}
                                            data-testid={`radar-type-toggle-${t.key}`}
                                            className={`h-9 px-3 text-xs font-semibold border transition-colors ${
                                                active ? "bg-black text-white border-black" : "bg-white text-gray-500 border-gray-300 hover:border-black"
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </section>

                        {/* Thresholds */}
                        <section data-testid="radar-thresholds-section">
                            <div className={overline + " mb-3"}>Soglie</div>
                            <div className="mb-5">
                                <div className="flex justify-between text-sm mb-2">
                                    <Label className="font-semibold">Score minimo per import</Label>
                                    <span className="font-mono-tight font-bold">{settings.min_score_for_import}</span>
                                </div>
                                <Slider
                                    value={[settings.min_score_for_import]}
                                    min={0} max={100} step={5}
                                    onValueChange={(v) => setSettings((s) => ({ ...s, min_score_for_import: v[0] }))}
                                    data-testid="radar-min-score-slider"
                                />
                                <p className="text-xs text-gray-500 mt-1.5">
                                    Le aste sotto questo score vengono auto-scartate (restano consultabili nel filtro &quot;Scartate&quot;).
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">Prezzo minimo €</Label>
                                    <Input
                                        type="number"
                                        value={settings.min_price}
                                        onChange={(e) => setSettings((s) => ({ ...s, min_price: Number(e.target.value) }))}
                                        className="mt-1.5"
                                        data-testid="radar-min-price"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">Prezzo massimo €</Label>
                                    <Input
                                        type="number"
                                        value={settings.max_price}
                                        onChange={(e) => setSettings((s) => ({ ...s, max_price: Number(e.target.value) }))}
                                        className="mt-1.5"
                                        data-testid="radar-max-price"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Frequency */}
                        <section data-testid="radar-frequency-section">
                            <div className={overline + " mb-3"}>Frequenza scansione</div>
                            <div className="flex items-center justify-between border border-gray-200 px-4 py-3">
                                <div>
                                    <div className="text-sm font-semibold">Scansione automatica giornaliera</div>
                                    <div className="text-xs text-gray-500">Una scansione al giorno delle fonti attive.</div>
                                </div>
                                <Switch
                                    checked={!!settings.daily_auto_scan}
                                    onCheckedChange={(v) => setSettings((s) => ({ ...s, daily_auto_scan: v }))}
                                    data-testid="radar-daily-scan-toggle"
                                />
                            </div>
                            {settings.daily_auto_scan && (
                                <div className="mt-3 flex items-center gap-3">
                                    <Label className="text-xs uppercase tracking-wider font-bold text-gray-600">Ora (UTC)</Label>
                                    <Input
                                        type="number" min={0} max={23}
                                        value={settings.daily_scan_hour_utc}
                                        onChange={(e) => setSettings((s) => ({ ...s, daily_scan_hour_utc: Math.max(0, Math.min(23, Number(e.target.value))) }))}
                                        className="w-24"
                                        data-testid="radar-scan-hour"
                                    />
                                </div>
                            )}
                        </section>

                        {/* Request log */}
                        <section data-testid="radar-request-log-section">
                            <div className={overline + " mb-3"}>Log ultime richieste</div>
                            {logs.length === 0 ? (
                                <p className="text-xs text-gray-400">Nessuna richiesta registrata.</p>
                            ) : (
                                <div className="border border-gray-200 divide-y divide-gray-100 max-h-52 overflow-y-auto">
                                    {logs.map((l, i) => (
                                        <div key={i} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                                            <span className={`font-mono-tight font-bold w-10 ${l.status >= 400 || l.status === 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                {l.status || "ERR"}
                                            </span>
                                            <span className="text-gray-400 w-32 shrink-0">{l.ts ? new Date(l.ts * 1000).toLocaleString("it-IT") : "—"}</span>
                                            <span className="text-gray-700 truncate flex-1">{l.url}</span>
                                            <span className="text-gray-400 w-12 text-right">{l.elapsed_ms}ms</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Save */}
                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="radar-settings-save-btn"
                                className="h-11 px-6 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                            >
                                {saving ? "Salvataggio…" : "Salva impostazioni"}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

function StatusTile({ icon: Icon, label, value, spin }) {
    return (
        <div className="border border-gray-200 p-3">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1">
                <Icon size={13} weight="bold" className={spin ? "animate-spin" : ""} />
                <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
            </div>
            <div className="text-sm font-semibold text-gray-900 truncate">{value}</div>
        </div>
    );
}
